import {
  Box,
  Button,
  CircularProgress,
  Step,
  StepLabel,
  Stepper,
  Typography,
  styled,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
import { base64ToFile, showToast } from "../../components/common/Util/Util";
import IncompleteLoanModal from "../../components/modals/IncompleteLoanModal";
import EntityBankDetailsDocUploadForm from "../../components/forms/EntityBorrower/BankDetailsDocUploadForm";
import EntityBasicDetailsForm from "../../components/forms/EntityBorrower/BasicDetailsForm";
import EntityDocUploadForm from "../../components/forms/EntityBorrower/EntityDocUploadForm";
import PromoterKYCDocUploadForm from "../../components/forms/EntityBorrower/PromoterKYCDocUploadForm";
import EntityRequirementBriefForm from "../../components/forms/EntityBorrower/RequirementBriefForm";
import EntityDocuments from "../../components/forms/EntityBorrower/EntityDocuments";
import IndividualBankDetailsDocUploadForm from "../../components/forms/IndividualBorrower/BankDetailsDocUploadForm";
import IndividualBasicDetailsForm from "../../components/forms/IndividualBorrower/BasicDetailsForm";
import IndividualKYCDocUploadForm from "../../components/forms/IndividualBorrower/KYCDocUploadForm";
import IndividualRequirementBriefForm from "../../components/forms/IndividualBorrower/RequirementBriefForm";
import {
  BORROWER_TYPES,
  ENTITY_TYPES,
  USER_TYPES,
} from "../../utils/constants";
import PreviewApplication from "./PreviewApplication";
import {
  activeSteps,
  selectFiles,
  selectFormData,
  setActiveStep,
  setFiles,
  setFormData,
  setResumed,
  updateFormField,
} from "../../Store/userSlice";
import { store } from "../../Store/Store";
import { 
  applyLoan, 
  getResumedApplications, 
  notify, 
} from "../../services/loanService";
import ThankYouPopup from "./ThankYouPopup";
import SaveDraftConfirmationDialog from "../../components/modals/SaveDraftConfirmationDialog";
import useFormProtection from "../../hooks/useFormProtection";

const PageWrapper = styled(Box)({
  backgroundColor: "#F5F6F7",
  minHeight: "100vh",
  padding: 0,
  margin: 0,
  display: "flex",
  justifyContent: "center",
});

const ContentContainer = styled(Box)({
  backgroundColor: "#FFFFFF",
  width: "100%",
  maxWidth: "1200px",
  padding: "24px 32px",
  boxShadow: "0px 0px 4px rgba(0, 0, 0, 0.05)",
  border: "1px solid #E5E8EB",
  margin: "24px 16px",
  borderRadius: "10px",
});

const PageTitle = styled(Typography)({
  color: "#0C53B7",
  fontFamily: "'Open Sans', sans-serif",
  fontWeight: 600,
  fontSize: "24px",
  lineHeight: "100%",
  letterSpacing: "0%",
  marginBottom: "8px",
});

const EntityTypeLabel = styled(Typography)({
  backgroundColor: "#EEF2FF",
  color: "#0052CC",
  padding: "8px 16px",
  borderRadius: "6px",
  fontWeight: 600,
  fontSize: "14px",
  display: "inline-block",
  marginBottom: "24px",
  marginTop: "8px",
});

const StyledButton = styled(Button)({
  backgroundColor: "#0052CC",
  color: "white",
  padding: "10px 24px",
  fontWeight: 600,
  textTransform: "none",
  marginTop: "24px",
  borderRadius: "8px",
  "&:hover": {
    backgroundColor: "#003E99",
  },
});

const FormWrapper = styled(Box)({
  marginTop: "32px",
});

const NavigationButtons = styled(Box)({
  display: "flex",
  justifyContent: "space-between",
  marginTop: "24px",
});

const HeaderSection = styled(Box)({
  display: "flex",
  justifyContent: "flex-start",
  alignItems: "center",
  marginBottom: "16px",
  paddingBottom: "0px",
  borderBottom: "none",
});

const BackButton = styled(Button)({
  color: "#6B7280",
  textTransform: "none",
  fontWeight: 500,
  padding: "4px 8px",
  minWidth: "auto",
  fontSize: "14px",
  "&:hover": {
    backgroundColor: "#F3F4F6",
    color: "#374151",
  },
});

const ApplyLoan = () => {
  const [modalOpen, setModalOpen] = useState(false);
  const [resumeApiResponse, setResumeApiResponse] = useState(null);
  const [entityType, setEntityType] = useState("");
  const [businessType, setBusinessType] = useState("");
  const [userEntityTypeLabel, setUserEntityTypeLabel] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [showApplicationPreviewer, setShowApplicationPreviewer] =
    useState(false);
  const [showThankYouPopUp, setShowThankYouPopUp] = useState(false);
  const [showNavigationButtons, setShowNavigationButtons] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const formRef = React.useRef();
  const dispatch = useDispatch();
  const formData = useSelector(selectFormData);
  const files = useSelector(selectFiles);
  const activeStep = useSelector(activeSteps);
  const ip = useSelector((state) => state.user.ip);
  const cordinates = useSelector((state) => state.user.cordinates);
  const loanApplicantUserDetails = JSON.parse(
    localStorage.getItem("loanApplicationUser") || "{}"
  );
  const userFromRedux = useSelector((state) => state.user?.user);
  // Use our form protection hook
  const {
    showDraftDialog,
    savingDraft,
    actionType,
    handleNavigationAttempt,
    handleSaveDraft,
    handleCancelSaveDraft,
    handleAutoSaveDraft,
    setShowDraftDialog,
    shouldProtectNavigation,
  } = useFormProtection(true); // true indicates this is the ApplyLoan page  

  // Listen for navigation events from DashboardLayout
  useEffect(() => {
    const handleBeforeNavigate = async (event) => {
      if (shouldProtectNavigation) {
        const { detail } = event;
        if (detail && detail.target) {
          // Prevent default navigation
          event.preventDefault();

          if (detail.onSaveComplete) {
            try {
              const saveResult = await handleSaveDraft();
              if (saveResult) {
                detail.onSaveComplete();
              } else {
                showToast("Failed to save draft", "error");
                detail.onSaveComplete();
              }
            } catch (error) {
              showToast("Failed to save draft", "error");
              detail.onSaveComplete();
            }
          } else {
            handleNavigationAttempt(detail.target, "navigation");
          }
        }
      } else {
        const { detail } = event || {};
        if (detail?.target) {
          detail.onSaveComplete?.() ?? navigate(detail.target === 'back' ? -1 : detail.target);
        }
      }
    };

    // Listen for navigation events from dashboard
    window.addEventListener("beforeNavigate", handleBeforeNavigate);

    return () => {
      window.removeEventListener("beforeNavigate", handleBeforeNavigate);
    };
  }, [shouldProtectNavigation, handleNavigationAttempt, handleSaveDraft, navigate]);

  // Listen for logout events
  useEffect(() => {
    const handleBeforeLogout = (event) => {
      if (shouldProtectNavigation) {
        // Prevent default logout 
        event?.preventDefault?.();
        event?.stopPropagation?.();
        event?.stopImmediatePropagation?.();
        // Show our draft dialog
        handleNavigationAttempt(null, "logout");
      }
    };

    // Listen for logout events
    window.addEventListener("beforeLogout", handleBeforeLogout);

    return () => window.removeEventListener("beforeLogout", handleBeforeLogout);
  }, [shouldProtectNavigation, handleNavigationAttempt]);

  // Listen for session timeout events to autosave
  useEffect(() => {
    const handleSessionTimeout = async () => {
      const currentToken = localStorage.getItem("jwtToken");
      const userBusinessType = localStorage.getItem("userBusinessType");
      const userEntityType = localStorage.getItem("userEntityType");
      const userInfo = localStorage.getItem("user");

      if (shouldProtectNavigation) {
        try {
          try {
            const saveResult = await handleAutoSaveDraft();

            if (saveResult) {
              showToast("Your application draft has been saved.", "success");
            } else {
              showToast(
                "There was an issue saving your application draft.",
                "warning"
              );
            }
          } catch (saveError) {
            showToast(
              "There was an error saving your application draft.",
              "error"
            );
          } finally {
            localStorage.removeItem("sessionTimeoutInProgress");

            if (currentToken) localStorage.setItem("jwtToken", currentToken);
            if (userBusinessType)
              localStorage.setItem("userBusinessType", userBusinessType);
            if (userEntityType)
              localStorage.setItem("userEntityType", userEntityType);
            if (userInfo) localStorage.setItem("user", userInfo);
          }
        } catch (error) {
          showToast("Failed to auto-save your application.", "error");
          localStorage.removeItem("sessionTimeoutInProgress");

          if (currentToken) localStorage.setItem("jwtToken", currentToken);
          if (userBusinessType)
            localStorage.setItem("userBusinessType", userBusinessType);
          if (userEntityType)
            localStorage.setItem("userEntityType", userEntityType);
          if (userInfo) localStorage.setItem("user", userInfo);
        }
      } else {
        localStorage.removeItem("sessionTimeoutInProgress");

        if (currentToken) localStorage.setItem("jwtToken", currentToken);
        if (userBusinessType)
          localStorage.setItem("userBusinessType", userBusinessType);
        if (userEntityType)
          localStorage.setItem("userEntityType", userEntityType);
        if (userInfo) localStorage.setItem("user", userInfo);
      }
    };

    // Listen for session timeout
    window.addEventListener("sessionTimeout", handleSessionTimeout);

    return () => {
      window.removeEventListener("sessionTimeout", handleSessionTimeout);

      // Get current tokens before component unmounts
      const currentToken = localStorage.getItem("jwtToken");
      const userBusinessType = localStorage.getItem("userBusinessType");
      const userEntityType = localStorage.getItem("userEntityType");
      const userInfo = localStorage.getItem("user");

      // Only clear the session timeout flag when component unmounts, not authentication data
      localStorage.removeItem("sessionTimeoutInProgress");

      // Restore auth tokens to ensure they're not cleared when unmounting
      if (currentToken) localStorage.setItem("jwtToken", currentToken);
      if (userBusinessType)
        localStorage.setItem("userBusinessType", userBusinessType);
      if (userEntityType)
        localStorage.setItem("userEntityType", userEntityType);
      if (userInfo) localStorage.setItem("user", userInfo);
    };
  }, [shouldProtectNavigation, handleAutoSaveDraft, formData]);

  const determineUserType = () => {
    const storedBusinessType = (localStorage.getItem("userBusinessType") || "").toLowerCase();
    const storedEntityType = (localStorage.getItem("userEntityType") || "").toLowerCase();
    const userType = String(
      loanApplicantUserDetails?.userType ||
      loanApplicantUserDetails?.borrowerType ||
      userFromRedux?.BorrowerType ||
      userFromRedux?.borrowerType ||
      storedBusinessType ||
      ""
    ).toLowerCase();

    const rawEntity = String(
      loanApplicantUserDetails?.entityType ||
      userFromRedux?.EntityType ||
      userFromRedux?.entityType ||
      storedEntityType ||
      ""
    ).toLowerCase();

    const normalizedEntity = 
      rawEntity === "company" ? "company" :
      rawEntity === "partnership" ? "partnership_firm" :
      rawEntity === "proprietorship" ? "propreitorship_firm" :
      rawEntity;

    const finalBorrowerType = userType === BORROWER_TYPES.ENTITY ? BORROWER_TYPES.ENTITY : BORROWER_TYPES.INDIVIDUAL;
    setBusinessType(finalBorrowerType);

    dispatch(updateFormField({
      field: "borrowerType",
      value: finalBorrowerType.toLowerCase()
    }));

    if (userType === BORROWER_TYPES.ENTITY || normalizedEntity) {
      const entityConfig = {
        company: { type: ENTITY_TYPES.COMPANY, label: "COMPANY", value: "company" },
        partnership_firm: { type: ENTITY_TYPES.PARTNERSHIP_FIRM, label: "PARTNERSHIP FIRM", value: "partnership_firm" },
        partnership: { type: ENTITY_TYPES.PARTNERSHIP_FIRM, label: "PARTNERSHIP FIRM", value: "partnership_firm" },
        propreitorship_firm: { type: ENTITY_TYPES.PROPRIETORSHIP_FIRM, label: "PROPRIETORSHIP FIRM", value: "propreitorship_firm" },
        proprietorship: { type: ENTITY_TYPES.PROPRIETORSHIP_FIRM, label: "PROPRIETORSHIP FIRM", value: "propreitorship_firm" }
      };

      const config = entityConfig[normalizedEntity] || { 
        type: normalizedEntity || "", 
        label: normalizedEntity?.toUpperCase() || "INDIVIDUAL", 
        value: normalizedEntity || "" 
      };

      setEntityType(config.type);
      setUserEntityTypeLabel(config.label);
      dispatch(updateFormField({ field: "entityType", value: config.value }));
    } else {
      setEntityType(BORROWER_TYPES.INDIVIDUAL.toLowerCase());
      setUserEntityTypeLabel("INDIVIDUAL");
      dispatch(updateFormField({ field: "entityType", value: "" }));
    }

    localStorage.setItem("userBusinessType", userType || BORROWER_TYPES.INDIVIDUAL);
    localStorage.setItem("userEntityType", 
      userType === BORROWER_TYPES.ENTITY ? normalizedEntity || "" : BORROWER_TYPES.INDIVIDUAL
    );
  };

  useEffect(() => {
    determineUserType();
    const userId = userFromRedux?.ID || loanApplicantUserDetails?.id;
    const isResumingFromResumePage = location.state?.fromResumePage === true;
    const isNewApplication =
    new URLSearchParams(location.search).get("new") === "true";
    const fromConsultantDashboard = 
      location.state?.fromConsultantDashboard === true || 
      location.state?.source === "consultant";

    if (
      userId && 
      !isResumingFromResumePage && 
      !fromConsultantDashboard &&
      !modalOpen
    ) {
      setIsLoading(true);
      getResumedApplications(userId)
        .then((res) => {
          if (
            res?.statusCode === 200 &&
            res?.message === "You have a pending loan application"
          ) {
            setResumeApiResponse(res);
            setModalOpen(true);
            dispatch(setResumed(true));}
        })
        .catch((error) => {
          console.error("Error checking for resumed applications:", error);         
        })
        .finally(() => {
          setIsLoading(false);
        });
    }
  }, [location, userFromRedux, dispatch]);

  const handleCloseModal = () => {
    dispatch(setFormData());
    dispatch(setFiles());
    dispatch(setActiveStep(0));
    setModalOpen(false);
  };

  const handleNext = async () => {
    const isValid = await formRef.current?.validateForm();
    if (isValid) {
      dispatch(setActiveStep(activeStep + 1));
      window.scrollTo(0, 0);
      setTimeout(() => {
        ensureAuthTokensExist();
      }, 0);
    }
  };

  const handleBack = () => {
    dispatch(setActiveStep(activeStep - 1));
    window.scrollTo(0, 0);

    setTimeout(() => {
      ensureAuthTokensExist();
    }, 0);
  };

  const handleBackToPreviousPage = () => {
    const isFromResumePage = location.state?.fromResumePage === true;
    const target = isFromResumePage ? "/loans" : "back";
    
    if (shouldProtectNavigation) {
      handleNavigationAttempt(target, "navigation");
    } else {
      navigate(isFromResumePage ? "/loans" : -1);
    }
  };

  const convertAndSetfile = (fieldName, fileData) => {
    if (fileData?.base64 && fileData?.filename) {
      try {
        // Assuming base64ToFile is your function that converts base64 to a file
        const fileObj = base64ToFile(fileData.base64, fileData.filename);
        return fileObj;
      } catch (error) {
        console.error("Error converting base64 to file:", error);
        return null;
      }
    } else if (
      fileData?.remote === true &&
      (fileData?.url || fileData?.documentPath)
    ) {
      const url = fileData.url || fileData.documentPath;
      const filename =
        fileData.filename ||
        `${fieldName}_remote_file${url.substring(url.lastIndexOf("."))}`;

      try {
        const emptyBlob = new Blob(["dummy content"], {
          type: "application/octet-stream",
        });
        const placeholderFile = new File([emptyBlob], filename, {
          type: "application/octet-stream",
        });
        return placeholderFile;
      } catch (error) {
        return null;
      }
    } else {
      return null;
    }
  };

  const handleSubmit = async (e) => {
    ensureAuthTokensExist();
    const isValid = await formRef.current?.validateForm();
    if (isValid) {
      setIsLoading(true);
      e.preventDefault(); // Prevent default form submission behavior

      try {
        const data = new FormData();
        const localStorageUser = JSON.parse(
          localStorage.getItem("user") || "{}"
        );
        const addr = localStorageUser?.address || "";
        const userEmail = formData?.email || userFromRedux?.Email || loanApplicantUserDetails?.email || "";
        const userId = userFromRedux?.ID || userFromRedux?.id || loanApplicantUserDetails?.ID || loanApplicantUserDetails?.id ||formData?.userId;
         const storedBusinessType = localStorage.getItem("userBusinessType");
         const storedEntityType = localStorage.getItem("userEntityType");
         
         const userBorrowerType = (
           storedBusinessType ||
           userFromRedux?.BorrowerType ||
           userFromRedux?.borrowerType ||
           ""
         ).toLowerCase();

         const fromDetail = {
           ...formData,
           userId,
           userType: USER_TYPES.BORROWER,
           borrowerType: userBorrowerType || BORROWER_TYPES.INDIVIDUAL,
           status: "submitted",
           ip,
           longitude: String(cordinates?.lng ?? ""),
           latitude: String(cordinates?.lat ?? ""),
           mobileNo: formData?.mobileNo || userFromRedux?.MobileNumber || userFromRedux?.mobileNumber || loanApplicantUserDetails?.MobileNumber,
           email: userEmail,
           addr,
           industry: formData?.industry || "",
           sector: formData?.sector || "",
         };

         if (fromDetail.borrowerType === BORROWER_TYPES.ENTITY.toLowerCase()) {
           let entityType = storedEntityType || ENTITY_TYPES.COMPANY;
           
           if (entityType === "borrower" || entityType === "individual") {
             const userEntityType = 
             userFromRedux?.EntityType || userFromRedux?.entityType || "";
             if (userEntityType) {
               const lowerEntityType = userEntityType.toLowerCase();
               if (
                lowerEntityType === "partnership_firm" || 
                lowerEntityType === "partnership"
              ) {
                 entityType = ENTITY_TYPES.PARTNERSHIP_FIRM;
               } else if (
                lowerEntityType === "propreitorship_firm" || 
                lowerEntityType === "proprietorship" || 
                lowerEntityType === "propreitorship_firm"
              ) {
                 entityType = ENTITY_TYPES.propreitorship_firm;
               } else if (
                lowerEntityType === "company" || 
                lowerEntityType === "private company"
              ) {
                 entityType = ENTITY_TYPES.COMPANY;
               }
             }
           }
           
           fromDetail.entityType = entityType;
         }

        if (
          Object.prototype.hasOwnProperty.call(
            fromDetail, 
            "anticipatedGrowthRateCurrentYear"
          )
        ) {
          fromDetail.anticipatedProjectGrowthRateForCurrentYear = 
          fromDetail.anticipatedGrowthRateCurrentYear;
          delete fromDetail.anticipatedGrowthRateCurrentYear;
        }

        delete fromDetail.address;
        delete fromDetail.Address;

        Object.entries(fromDetail).forEach(([key, value]) => {
          if (value !== undefined && value !== null && key !== "documents") {
            data.append(key, value);
          }
        });

        const processedFields = new Set();

        if (files.AadharCard || files.aadharCard) {
          const aadharFiles = files.AadharCard || files.aadharCard;

          if (Array.isArray(aadharFiles)) {
            aadharFiles.forEach((file) => {
              if (file) {
                const fileObj = convertAndSetfile("AadharCard", file);
                if (fileObj) {
                  data.append(
                    "AadharCard",
                    fileObj,
                    fileObj.name || "AadharCard"
                  );
                }
              }
            });
          } else if (aadharFiles) {
            const fileObj = convertAndSetfile("AadharCard", aadharFiles);
            if (fileObj) {
              data.append("AadharCard", fileObj, fileObj.name || "AadharCard");
            }
          }

          // Mark as processed
          processedFields.add("AadharCard");
          processedFields.add("aadharCard");
          processedFields.add("aadharPartner");
        }

        // Handle files, with special treatment for array fields
        Object.entries(files).forEach(([key, value]) => {
          if (processedFields.has(key)) return;
          if (Array.isArray(value)) {
            processedFields.add(key);
            // Handle array fields
            if (
              key === "applicantItr" ||
              key === "coApplicantItr" ||
              key === "bankStatement" ||
              key === "slLoan" ||
              key === "salarySlips" ||
              key === "itr" ||
              key === "balanceSheet"
            ) {
              const filteredValue = value.filter((item) => item !== null);

              if (key === "bankStatement" || key === "slLoan") {
                filteredValue.forEach((file, index) => {
                  if (file) {
                    const fileObj = convertAndSetfile(key, file);
                    if (fileObj) {
                      const documentIndex = index + 1;
                      const documentName = `${
                        key.charAt(0).toUpperCase() + key.slice(1)
                      }-${documentIndex}`;

                      data.append(key, fileObj, fileObj.name || documentName);
                    }
                  }
                });
              } else {
                filteredValue.forEach((file) => {
                  if (file) {
                    const fileObj = convertAndSetfile(key, file);
                    if (fileObj) {
                      data.append(key, fileObj, fileObj.name || key);
                    }
                  }
                });
              }
            } else {
              // Regular arrays (like panCard, AadharCard)
              if (key === "AadharCard") {
                processedFields.add("aadharCard");
              } else if (key === "aadharCard") {
                processedFields.add("AadharCard");
              } else if (key === "panPartner") {
                processedFields.add("panCard");
                value.forEach((file) => {
                  if (file) {
                    const fileObj = convertAndSetfile(key, file);
                    if (fileObj) {
                      data.append(
                        "panCard",
                        fileObj,
                        fileObj.name || "panCard"
                      );
                    }
                  }
                });
              } else if (key === "aadharPartner") {
                processedFields.add("aadharPartner");
                processedFields.add("AadharCard");
                processedFields.add("aadharCard");

                value.forEach((file) => {
                  if (file) {
                    const fileObj = convertAndSetfile(key, file);
                    if (fileObj) {
                      data.append(
                        "AadharCard",
                        fileObj,
                        fileObj.name || "AadharCard"
                      );
                    }
                  }
                });
              } else if (key === "AadharCard" || key === "aadharCard") {
                processedFields.add("aadharPartner");
                processedFields.add("AadharCard");
                processedFields.add("aadharCard");

                value.forEach((file) => {
                  if (file) {
                    const fileObj = convertAndSetfile(key, file);
                    if (fileObj) {
                      data.append(
                        "AadharCard",
                        fileObj,
                        fileObj.name || "AadharCard"
                      );
                    }
                  }
                });
              } else {
                value.forEach((file) => {
                  if (file) {
                    const fileObj = convertAndSetfile(key, file);
                    if (fileObj) {
                      data.append(key, fileObj, fileObj.name || key);
                    }
                  }
                });
              }
            }
          } else if (value) {
            processedFields.add(key);
            const fileObj = convertAndSetfile(key, value);
            if (fileObj) {
              data.append(key, fileObj, fileObj.name || key);
            }
          }
        });
        ensureAuthTokensExist();

        //Step 4: Send to backend
        const res = await applyLoan(data);
        const statusOk = res?.statusCode === 200;
        const messageOk = res?.message === "Application Saved Successfully";
        if (statusOk && messageOk) {
          ensureAuthTokensExist();
          setShowApplicationPreviewer(false);
          showToast("Application submitted successfully!", "success");
          setShowThankYouPopUp(true);
          dispatch(setActiveStep(0));
          const notificationRequest = {
            userId: null,
            title: "New Loan Application Received",
            content: `A new loan application has been submitted by ${
              userFromRedux?.Name || userFromRedux?.name
            }.`,
            createdByRole: "USER",
            type: "loanFormSubmission",
            action: "review",
            actionUrl: "/loans",
            unread: true,
          };
          notify(notificationRequest).catch((err) => console.error(err));
          dispatch(setFormData({}));
          dispatch(setFiles({}));
          ensureAuthTokensExist();
        } 
      } catch (err) {
        console.error(err);
        ensureAuthTokensExist();
        showToast(
          "Something went wrong while submitting the application.",
          "error"
        );
      } finally {
        setIsLoading(false);
      }
    }
  };

  const individualSteps = [
    "Basic Details",
    "KYC Documents",
    "Bank Details",
    "Requirement Brief",
  ];

  const entitySteps = [
    "Basic Details",
    "Requirement Brief",
    "Entity Documents",
    "Promoter KYC",
    "Bank Details",
    "Security and Other Details",
  ];

  const steps =
    entityType === BORROWER_TYPES.INDIVIDUAL ? individualSteps : entitySteps;

  const getForm = () => {
    ensureAuthTokensExist();

    const isEntityUser =
      businessType.toLowerCase() === BORROWER_TYPES.ENTITY.toLowerCase();

    let formComponent = null;
    if (!isEntityUser) {
      // Individual flow (4 steps)
      switch (activeStep) {
        case 0: // Step 1: Basic Details
          formComponent = <IndividualBasicDetailsForm ref={formRef} />;
          break;
        case 1: // Step 2: KYC Documents
          formComponent = <IndividualKYCDocUploadForm ref={formRef} />;
          break;
        case 2: // Step 3: Bank Details
          formComponent = <IndividualBankDetailsDocUploadForm ref={formRef} />;
          break;
        case 3: // Step 4: Requirement Brief
          formComponent = <IndividualRequirementBriefForm ref={formRef} />;
          break;
        default:
          formComponent = null;
      }
    } else {
      // Entity user flow (5 steps as per the specification)
      const currentEntityType = 
      formData?.entityType || userEntityTypeLabel.toLowerCase();
      console.log("Current Entity Type:", currentEntityType);
      console.log('[APPLY-LOAN-DEBUG] currentEntityType changed to:', currentEntityType);
      // Proprietorship Firm
      switch (activeStep) {
        case 0: // Step 1: Basic Details (common for all entity types)
          formComponent = <EntityBasicDetailsForm ref={formRef} />;
          break;
        case 1: // Step 2: Entity Document Upload (common for all entity types)
          formComponent = <EntityDocUploadForm ref={formRef} handleBack={handleBack}/>;
          break;
        case 2: // Step 2.1: Entity-specific document upload
         formComponent = <EntityDocuments ref={formRef} currentEntityType={currentEntityType}/>;
          break;
        case 3: // Step 3: KYC Documents based on entity type
        formComponent = <PromoterKYCDocUploadForm ref={formRef} />;
          break;
        case 4: // Step 4: Bank Details (common for all entity types)
          formComponent = <EntityBankDetailsDocUploadForm ref={formRef} />;
          break;
        case 5: // Step 5: Requirement Brief (common for all entity types)
          formComponent = <EntityRequirementBriefForm ref={formRef} />;
          break;
        default:
          formComponent = null;
      }
    }

    setTimeout(() => {
      ensureAuthTokensExist();
    }, 0);

    return formComponent;
  };

  // Inside the ApplyLoan component, add a useEffect to ensure userId is set
  useEffect(() => {
    const borrowerUserId = loanApplicantUserDetails?.userId || loanApplicantUserDetails?.id || loanApplicantUserDetails?.ID;
    const fallbackUserId = userFromRedux?.userId || userFromRedux?.id || userFromRedux?.ID;
    const effectiveUserId = borrowerUserId || fallbackUserId;

    if (effectiveUserId && formData?.userId !== effectiveUserId) {
      dispatch(updateFormField({ field: "userId", value: effectiveUserId }));
    }
  }, [loanApplicantUserDetails, userFromRedux, formData?.userId, dispatch]);

  useEffect(() => {
    if (location.pathname === "/apply-loan" && !modalOpen) {
      const shouldClearForm = location.state?.directNavigation === true ||
        new URLSearchParams(location.search).get("new") === "true" ||
        location.state?.fromConsultantDashboard === true ||
        location.state?.source === "consultant";

      if (shouldClearForm) {
        const currentFormData = store.getState().user.formData;
        const preservedData = {
          entityType: currentFormData.entityType,
          borrowerType: currentFormData.borrowerType,
          userId: currentFormData.userId
        };
        dispatch(setFormData(preservedData));
        dispatch(setFiles({}));
        dispatch(setActiveStep(0));
      }

      const tokens = {
        jwtToken: localStorage.getItem("jwtToken"),
        userBusinessType: localStorage.getItem("userBusinessType"),
        userEntityType: localStorage.getItem("userEntityType"),
        user: localStorage.getItem("user")
      };

      Object.entries(tokens).forEach(([key, value]) => {
        if (value) localStorage.setItem(key, value);
      });
    }
  }, [
    location.pathname, 
    dispatch, 
    modalOpen, 
    location?.state, 
    location?.search,
  ]);

  const ensureAuthTokensExist = () => {
    const token = userFromRedux?.jwtToken;
    const userType = userFromRedux?.entityType || userFromRedux?.UserType;
    const businessType =
      userFromRedux?.borrowerType || userFromRedux?.BorrowerType;

    if (token && !localStorage.getItem("jwtToken")) {
      localStorage.setItem("jwtToken", token);
    }

    if (userType && !localStorage.getItem("userEntityType")) {
      localStorage.setItem("userEntityType", userType);
    }

    if (businessType && !localStorage.getItem("userBusinessType")) {
      localStorage.setItem("userBusinessType", businessType);
    }
  };

  useEffect(() => {
    ensureAuthTokensExist();
  }, [userFromRedux]);

  useEffect(() => {
    const checkIfConsultantNavigation = () => {
      const userType = userFromRedux?.UserType || userFromRedux?.userType;
      const isConsultant = userType?.toLowerCase() === 'consultant';
      
      const userFromConsultantDashboard = 
        location.state?.fromConsultantDashboard === true ||
        location.state?.source === "consultant" ||
        (document.referrer && 
          document.referrer.includes("/dashboard") && 
          isConsultant);
      
      setShowNavigationButtons(isConsultant && userFromConsultantDashboard);
    };

    checkIfConsultantNavigation();
  }, [userFromRedux, location]);

  return (
    <PageWrapper>
      {isLoading ? (
        <ContentContainer
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            minHeight: "300px",
          }}
        >
          <CircularProgress />
          <Typography sx={{ ml: 2 }}>Loading application data...</Typography>
        </ContentContainer>
      ) : (
        <ContentContainer>
          {(showNavigationButtons || location.state?.fromResumePage === true) && (
            <HeaderSection>
              <BackButton
                onClick={handleBackToPreviousPage}
                startIcon={<ArrowBackIcon />}
                disabled={isLoading}
              >
                Back
              </BackButton>
            </HeaderSection>
          )}
          
          <PageTitle>Apply for Loan</PageTitle>

          <Typography sx={{ mb: 1 }}>
            {formData
              ? "Continuing your loan application. Please complete the remaining steps."
              : "Please complete all the required details to apply for a loan."}
          </Typography>

          <EntityTypeLabel>{userEntityTypeLabel}</EntityTypeLabel>

          <Stepper activeStep={activeStep} alternativeLabel sx={{ mt: 3 }}>
            {steps.map((label) => (
              <Step key={label}>
                <StepLabel>{label}</StepLabel>
              </Step>
            ))}
          </Stepper>

          <FormWrapper>
            {getForm()}

            <NavigationButtons>
              {activeStep === 0 ? (
                <Box />
              ) : (
                <Button
                  variant="outlined"
                  color="primary"
                  onClick={handleBack}
                  disabled={isLoading}
                >
                  Back
                </Button>
              )}
              {activeStep === steps.length - 1 ? (
                <StyledButton
                  variant="contained"
                  onClick={async () => {
                    const isValid = await formRef.current?.validateForm();
                    // If you're using Formik, it returns an object with errors. Check if it's empty:
                    if (isValid && Object.keys(isValid).length === 0) {
                      setShowApplicationPreviewer(true);
                    }
                  }}
                  disabled={isLoading}
                  startIcon={
                    isLoading && <CircularProgress size={20} color="inherit" />
                  }
                >
                  {isLoading ? "Submitting..." : "Preview & Submit"}
                </StyledButton>
              ) : (
                <StyledButton
                  variant="contained"
                  onClick={handleNext}
                  disabled={isLoading}
                >
                  Next
                </StyledButton>
              )}
            </NavigationButtons>
          </FormWrapper>
        </ContentContainer>
      )}

      {modalOpen && (
        <IncompleteLoanModal open={modalOpen} onClose={handleCloseModal} apiResponse={resumeApiResponse} />
      )}

      {/* Save Draft Confirmation Dialog */}
      {showDraftDialog && (
        <SaveDraftConfirmationDialog
          open={showDraftDialog}
          onClose={() => setShowDraftDialog(false)}
          onContinue={handleSaveDraft}
          onCancel={handleCancelSaveDraft}
          isProcessing={savingDraft}
          actionType={actionType}
        />
      )}
      {showApplicationPreviewer && (
        <PreviewApplication
          open={showApplicationPreviewer}
          onClose={() => setShowApplicationPreviewer(false)}
          handleSubmit={handleSubmit}
        />
      )}
      {showThankYouPopUp && (
        <ThankYouPopup
          showThankYouPopUp={showThankYouPopUp}
          setShowThankYouPopUp={setShowThankYouPopUp}
          from="apply-loan"
        />
      )}
    </PageWrapper>
  );
};

export default ApplyLoan;
